import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, IconButton, TextField, Tooltip } from "@mui/material";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { partnerQueryKeys } from "../../api/queryKeys";
import {
  confirmPartnerLead,
  emailLeadShareLink,
  fetchPartnerLeads,
  issueLeadShareLink,
} from "../../api/endpoints/leads";
import { EmptyState, PageHeader, StatusChip } from "../../components/ui";
import LeadEditorDrawer from "./LeadEditorDrawer";
import {
  canConfirmLead,
  canEditLead,
  canEmailClientLink,
  fullFormLabel,
  isAwaitingClient,
  leadStateTone,
  nextActionFor,
} from "./leadHelpers";
import { toast } from "react-toastify";

const CHIP_TONES = {
  default: "neutral",
  info: "sky",
  warning: "amber",
  success: "emerald",
  error: "rose",
};

const errorMessage = (err, fallback) =>
  err?.response?.data?.errorMessage || err?.response?.data?.message || fallback;

const copyToClipboard = async (text) => {
  if (!text || !navigator?.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    return false;
  }
};

const LeadsPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailNote, setEmailNote] = useState("");
  const [shareResult, setShareResult] = useState(null);

  const params = { page, size: 20 };
  const { data, isLoading, isError, refetch } = useQuery(
    partnerQueryKeys.leads(params),
    () => fetchPartnerLeads(params),
    { keepPreviousData: true, refetchOnMount: true }
  );
  const totalPages = data?.totalPages ?? 0;

  const refresh = () => {
    refetch();
    queryClient.invalidateQueries(partnerQueryKeys.leads());
  };

  const confirmMutation = useMutation((id) => confirmPartnerLead(id), {
    onSuccess: () => {
      toast.success("Lead sent to the office");
      setConfirmTarget(null);
      refresh();
    },
    onError: (err) => toast.error(errorMessage(err, "Could not send")),
  });

  const shareMutation = useMutation((id) => issueLeadShareLink(id), {
    onSuccess: async (url) => {
      const copied = await copyToClipboard(url);
      if (copied) {
        toast.success("Client link copied");
      } else {
        setShareResult(url);
      }
      refresh();
    },
    onError: (err) => toast.error(errorMessage(err, "Could not create the link")),
  });

  const emailMutation = useMutation(({ id, note }) => emailLeadShareLink(id, note), {
    onSuccess: () => {
      toast.success("Link emailed to the client");
      closeEmailDialog();
      refresh();
    },
    onError: (err) => toast.error(errorMessage(err, "Could not email the link")),
  });

  const rows = data?.content || [];

  const openEmailDialog = (lead) => {
    setEmailNote("");
    setEmailTarget(lead);
  };

  const closeEmailDialog = () => {
    setEmailTarget(null);
    setEmailNote("");
  };

  const openNew = () => {
    setEditingLead(null);
    setEditorOpen(true);
  };

  const openEdit = (lead) => {
    setEditingLead(lead);
    setEditorOpen(true);
  };

  const emailTooltip = (lead) => {
    if (!canEmailClientLink(lead)) {
      return "Add an email address to this lead before you can send them the form";
    }
    return `Email the form link to ${lead.email}, prefilled with what you entered`;
  };

  return (
    <div>
      <PageHeader
        eyebrow="Pipeline"
        title="New clients"
        subtitle="Start a client yourself, or send them a secure link and review what comes back."
        actions={
          <Button variant="contained" size="small" sx={{ bgcolor: "#0f766e" }} onClick={openNew}>
            New client
          </Button>
        }
      />

      {isError ? (
        <EmptyState
          title="Couldn't load your leads"
          description="Check your connection and try again."
        />
      ) : !isLoading && rows.length === 0 ? (
        <EmptyState
          title="No leads yet"
          description="Add your first client, or send a client the secure intake link."
          action={
            <Button variant="contained" size="small" onClick={openNew}>
              New client
            </Button>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3 font-semibold">Name</th>
                <th className="px-4 py-3 font-semibold">Contact</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Next step</th>
                <th className="px-4 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((lead) => (
                <tr key={lead.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium">
                    {lead.convertedClientId ? (
                      <Link
                        to={`/partner/clients/${lead.convertedClientId}`}
                        className="text-teal-700 no-underline hover:underline"
                      >
                        {lead.name || "—"}
                      </Link>
                    ) : (
                      lead.name || "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{lead.email || lead.number || "—"}</td>
                  <td className="px-4 py-3">
                    <StatusChip tone={CHIP_TONES[leadStateTone(lead.state)] || "neutral"}>
                      {lead.state}
                    </StatusChip>
                    {lead.state === "REJECTED" && lead.rejectionReason ? (
                      <p className="text-xs text-rose-600 m-0 mt-1">{lead.rejectionReason}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{nextActionFor(lead)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {canEditLead(lead) ? (
                        <Button size="small" variant="outlined" onClick={() => openEdit(lead)}>
                          Edit
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        variant={lead.state === "CLIENT_SUBMITTED" ? "contained" : "outlined"}
                        color={lead.state === "CLIENT_SUBMITTED" ? "warning" : "primary"}
                        onClick={() => navigate(`/partner/leads/${lead.id}/form`)}
                      >
                        {fullFormLabel(lead)}
                      </Button>
                      {canEditLead(lead) ? (
                        <Tooltip title="Copy the client's form link to share it yourself">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label="Copy the client's form link"
                              disabled={shareMutation.isLoading}
                              onClick={() => shareMutation.mutate(lead.id)}
                            >
                              <ContentCopyOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {canEditLead(lead) ? (
                        <Tooltip title={emailTooltip(lead)}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label="Email the form link to the client"
                              disabled={!canEmailClientLink(lead) || emailMutation.isLoading}
                              onClick={() => openEmailDialog(lead)}
                            >
                              <MailOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      {canConfirmLead(lead) ? (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => setConfirmTarget(lead)}
                        >
                          Send to office
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 border-t border-slate-100 px-4 py-3">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-slate-500">
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((current) => current + 1)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          ) : null}
        </div>
      )}

      <LeadEditorDrawer
        open={editorOpen}
        lead={editingLead}
        onClose={() => {
          setEditorOpen(false);
          setEditingLead(null);
        }}
        onSaved={refresh}
      />

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        title="Send this lead to the office?"
        message={
          <>
            {isAwaitingClient(confirmTarget) ? (
              <span className="block mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
                The form was sent to {confirmTarget?.name} but they have not submitted it yet.
                Please confirm the details yourself before sending. Their link stops working once
                you send.
              </span>
            ) : null}
            <span className="block">
              {confirmTarget?.name} will appear in the organisation's activation queue. You cannot
              change it after sending.
            </span>
          </>
        }
        confirmLabel="Send to office"
        loading={confirmMutation.isLoading}
        onConfirm={() => confirmMutation.mutate(confirmTarget.id)}
        onCancel={() => setConfirmTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(emailTarget)}
        title="Email the form to this client?"
        message={
          <>
            <span className="block">
              {emailTarget?.name} will get a secure link at {emailTarget?.email}, already filled in
              with the details you entered, along with your contact details as their referrer.
            </span>
            {emailTarget && emailTarget.state !== "DRAFT" ? (
              <span className="block mt-3 text-slate-500">
                Any link you sent them earlier stops working.
              </span>
            ) : null}
          </>
        }
        content={
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label="Note to the client (optional)"
            placeholder="Tell them why you are sending this and what to change."
            value={emailNote}
            inputProps={{ maxLength: 2000 }}
            onChange={(event) => setEmailNote(event.target.value)}
          />
        }
        confirmLabel="Send email"
        loading={emailMutation.isLoading}
        onConfirm={() => emailMutation.mutate({ id: emailTarget.id, note: emailNote.trim() })}
        onCancel={closeEmailDialog}
      />

      <ConfirmDialog
        open={Boolean(shareResult)}
        title="Client link ready"
        message={shareResult || ""}
        confirmLabel="Copy link"
        onConfirm={() => {
          copyToClipboard(shareResult);
          setShareResult(null);
        }}
        onCancel={() => setShareResult(null)}
      />
    </div>
  );
};

export default LeadsPage;
